import { memo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaCheck, FaTimes } from 'react-icons/fa';
import InfoIcon from '@material-ui/icons/Info';
import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import useTapOrCoarsePointerTooltip from '../../../../hooks/useTapOrCoarsePointerTooltip';
import Label from '../../../Label';
import MarkdownToHtml from '../../../MarkdownToHtml';
import { RefundTypes } from '../../../../constants';
import { checkDoShowTripConfirmationPricing } from '../../../../store/slices/user';
import { removePricingInformation } from '../../../../helpers/formatters/formatRoomDescription';

// TODO: Consider replacing the default `Label`-based variant with the plain presentation (inline
// text + optional tooltip affordance) everywhere it reads better, then drop `variant` if only one
// remains. Requires regression testing all consumers: TripConfiguration/HotelInfo, HotelPageInfo/
// HotelRate, RoomsDetails (grouped rates), and every product/refund/cancellationPolicy state.

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    maxWidth: '350px',
  },
  arrow: {
    color: theme.palette.common.white,
  },
}))(Tooltip);

const cancellationTooltipContent = (product, doShowPricingToClients) => {
  const content = `${product.cancellationPolicy || ''}${
    product.cancellationPenaltyDescription
      ? `<br/><b>Penalty description: </b>${product.cancellationPenaltyDescription}`
      : ''
  }`;

  return doShowPricingToClients ? content : removePricingInformation(content);
};

const HotelCancellationLabel = ({ product, variant }) => {
  const { tapMode, tooltipProps, triggerBindings } = useTapOrCoarsePointerTooltip();
  const doShowPricingToClients = useSelector(checkDoShowTripConfirmationPricing);

  if (!product) return <></>;

  const refundLabel =
    product.refundType === RefundTypes.FullyRefundable
      ? 'Refundable'
      : product.refundType === RefundTypes.PartialRefundable
        ? 'Refundable (penalty applies)'
        : 'Non-Refundable';

  const tooltipContent = cancellationTooltipContent(product, doShowPricingToClients);
  // Hiding pricing can empty the tooltip entirely (or leave only markup), so both variants
  // check for real text before offering a hover affordance.
  const hasTooltipContent = tooltipContent.replace(/<[^>]+>/g, '').trim().length > 0;

  if (variant === 'plain') {
    const showCancellationTip = hasTooltipContent;
    const refundText = (
      <span
        className={clsx(
          product.nonRefundable ? 'text-red-600' : 'text-green-800',
          showCancellationTip &&
            'border-b border-dotted border-gray-400 pb-px hover:border-gray-500',
        )}
      >
        {refundLabel}
      </span>
    );
    const body = showCancellationTip ? (
      <span className="inline-flex items-baseline cursor-help font-normal not-italic">
        {refundText}
        <sup className="ml-0.5 leading-none">
          <InfoIcon
            className={clsx('block', product.nonRefundable ? 'text-red-600' : 'text-green-800')}
            style={{ fontSize: '0.6875rem' }}
            aria-hidden="true"
          />
        </sup>
      </span>
    ) : (
      <span className="font-normal not-italic inline">{refundText}</span>
    );
    return showCancellationTip ? (
      <LightTooltip
        title={
          <MarkdownToHtml content={tooltipContent} className="text-xs font-normal text-black" />
        }
        placement="top"
        arrow
        {...tooltipProps}
      >
        {tapMode ? (
          <span className="inline-flex cursor-pointer" {...triggerBindings}>
            {body}
          </span>
        ) : (
          body
        )}
      </LightTooltip>
    ) : (
      body
    );
  }

  return (
    <Label
      text={
        <span>
          {refundLabel}
          <InfoIcon className="-mt-1 ml-1" style={{ fontSize: '1.25rem' }} />
        </span>
      }
      icon={product.nonRefundable ? <FaTimes /> : <FaCheck />}
      level={product.nonRefundable ? 'danger' : 'success'}
      italic
      inline
      descriptionOnHover={hasTooltipContent ? tooltipContent : undefined}
    />
  );
};

HotelCancellationLabel.propTypes = {
  product: PropTypes.shape({
    nonRefundable: PropTypes.bool,
    refundType: PropTypes.number,
    cancellationPolicy: PropTypes.string,
    cancellationPenaltyDescription: PropTypes.string,
    code: PropTypes.string,
  }).isRequired,
  variant: PropTypes.oneOf(['default', 'plain']),
};

HotelCancellationLabel.defaultProps = {
  variant: 'default',
};

export default memo(HotelCancellationLabel);
